import React, { useState, useEffect, useCallback } from "react";
import { Save, Plus, Trash2, Calendar } from "lucide-react";
import { supabase } from "../../lib/supabase";

interface NextSessionsProps {
  courseId?: string;
  initialData: any;
}

interface Session {
  id?: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  max_seats: number;
  is_virtual: boolean;
  display_session: boolean;
}

export default function NextSessionsSection({
  courseId,
  initialData,
}: NextSessionsProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);

  // Initialize sessions from initialData
  useEffect(() => {
    if (initialData?.course_sessions) {
      setSessions(
        initialData.course_sessions.map((session: any) => ({
          id: session.id,
          date: session.session_date
            ? new Date(session.session_date).toISOString().split("T")[0]
            : "",
          start_time: session.start_time || "",
          end_time: session.end_time || "",
          location: session.location || "",
          max_seats: session.max_seats || initialData.max_students || 20,
          is_virtual: session.is_virtual || false,
          display_session: session.display_session !== false, // default to true if undefined
        })),
      );
    } else {
      setSessions([]);
    }
  }, [initialData]);

  // Check for changes
  const checkForChanges = useCallback(() => {
    if (!initialData?.course_sessions) {
      setHasUnsavedChanges(sessions.length > 0);
      return;
    }

    const initialSessions = initialData.course_sessions;

    if (sessions.length !== initialSessions.length) {
      setHasUnsavedChanges(true);
      return;
    }

    const hasChanges = sessions.some((session, index) => {
      const initialSession = initialSessions[index];
      if (!initialSession) return true;

      const sessionDate = session.date
        ? new Date(session.date).toISOString().split("T")[0]
        : "";
      const initialDate = initialSession.session_date
        ? new Date(initialSession.session_date).toISOString().split("T")[0]
        : "";

      return (
        sessionDate !== initialDate ||
        session.start_time !== initialSession.start_time ||
        session.end_time !== initialSession.end_time ||
        session.location !== initialSession.location ||
        session.max_seats !== initialSession.max_seats ||
        session.is_virtual !== initialSession.is_virtual ||
        session.display_session !== initialSession.display_session
      );
    });

    setHasUnsavedChanges(hasChanges);
  }, [sessions, initialData]);

  useEffect(() => {
    checkForChanges();
  }, [sessions, checkForChanges]);

  const handleSave = async () => {
    if (!courseId) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Delete existing sessions
      if (initialData?.course_sessions?.length > 0) {
        const sessionIds = initialData.course_sessions.map(
          (session: any) => session.id,
        );

        const { error: deleteError } = await supabase
          .from("course_sessions")
          .delete()
          .in("id", sessionIds);

        if (deleteError) throw deleteError;
      }

      // Insert new sessions
      if (sessions.length > 0) {
        const sessionsToInsert = sessions.map((session) => ({
          course_id: courseId,
          session_date: session.date,
          start_time: session.start_time,
          end_time: session.end_time,
          location: session.location,
          max_seats: session.max_seats,
          is_virtual: session.is_virtual,
          display_session: session.display_session,
        }));

        const { data: insertedSessions, error: insertError } = await supabase
          .from("course_sessions")
          .insert(sessionsToInsert)
          .select();

        if (insertError) throw insertError;

        // Update initialData reference
        initialData.course_sessions = insertedSessions;
      } else {
        initialData.course_sessions = [];
      }

      setSuccessMessage("Sessions updated successfully");
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving sessions:", err);
      setError(err instanceof Error ? err.message : "Failed to save sessions");
    } finally {
      setSaving(false);
    }
  };

  const addSession = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    setSessions((prev) => [
      ...prev,
      {
        date: tomorrow.toISOString().split("T")[0],
        start_time: "09:00",
        end_time: "17:00",
        location: "Omaha Office",
        max_seats: initialData?.max_students || 20,
        is_virtual: false,
        display_session: true,
      },
    ]);
  };

  const updateSession = (index: number, field: keyof Session, value: any) => {
    setSessions((prev) =>
      prev.map((session, i) =>
        i === index ? { ...session, [field]: value } : session,
      ),
    );
  };

  const removeSession = (index: number) => {
    setSessions((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">Next Sessions</h2>
          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
              hasUnsavedChanges
                ? "text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                : "text-gray-500 bg-gray-100"
            } disabled:opacity-50 transition-colors duration-200`}
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? "Saving..." : "Save Sessions"}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        <div className="space-y-4">
          {sessions.map((session, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-gray-700">Session {index + 1}</span>
                </div>
                <button
                  onClick={() => removeSession(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <input
                    type="date"
                    value={session.date}
                    onChange={(e) =>
                      updateSession(index, "date", e.target.value)
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={session.start_time}
                      onChange={(e) =>
                        updateSession(index, "start_time", e.target.value)
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={session.end_time}
                      onChange={(e) =>
                        updateSession(index, "end_time", e.target.value)
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <input
                    type="text"
                    value={session.location}
                    onChange={(e) =>
                      updateSession(index, "location", e.target.value)
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                    placeholder="e.g., Omaha Office, Online, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max Seats
                  </label>
                  <input
                    type="number"
                    value={session.max_seats}
                    onChange={(e) =>
                      updateSession(
                        index,
                        "max_seats",
                        parseInt(e.target.value),
                      )
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`is_virtual_${index}`}
                    checked={session.is_virtual}
                    onChange={(e) =>
                      updateSession(index, "is_virtual", e.target.checked)
                    }
                    className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
                  />
                  <label
                    htmlFor={`is_virtual_${index}`}
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Virtual session
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`display_session_${index}`}
                    checked={session.display_session}
                    onChange={(e) =>
                      updateSession(index, "display_session", e.target.checked)
                    }
                    className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
                  />
                  <label
                    htmlFor={`display_session_${index}`}
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Display Session
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addSession}
          className="mt-4 w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <Plus className="h-5 w-5 inline-block mr-2" />
          Add Session
        </button>
      </div>
    </div>
  );
}
